/**
 * fire_volume.ts — the FireVolume shader family (fire realism R1, 2026-09-25).
 *
 * One design, four quality rungs (lib/vfx/fire_rules.ts picks the rung):
 *
 *   'volume'  THE WORKHORSE (med/high, both backends): a PROCEDURAL box march.
 *             A unit box (scaled to 1.3 × 2.3 × 1.3 m per campfire) drawn
 *             BackSide; the fragment enters the box along the view ray (a
 *             slab test in the box's own frame) and marches to the box exit
 *             OR THE OPAQUE DEPTH (`vfxSceneLinearDepth` — the VFX's OWN
 *             snapshot, scene_depth.ts, never three's shared one), so the logs and
 *             stones inside the box occlude the flame exactly and its base
 *             fades softly into them. `depthTest` is OFF on purpose: the back
 *             face sits BEHIND the logs, and a depth-tested back face would
 *             lose the whole flame in front of them — the march's own depth
 *             clamp does the occlusion. Density is Quilez domain-warped fBm
 *             from the baked 64³ noise volume (lib/vfx/noise3d.ts): one fetch
 *             is a vec3 warp, a second the density; a teardrop taper narrows
 *             with height so tongues detach; the weather wind leans it
 *             (∝ y²). Temperature → the 256-texel BLACKBODY LUT over
 *             800–1,600 K (lib/vfx/blackbody.ts), radiance ∝ T⁴, exposure
 *             tuned so the core lands at 8–20 in linear HDR (the tone map
 *             whitens it, the bloom picks it up). Cool soot ABSORBS and is
 *             lit grey by the sky (`uSmokeLight`) and orange from beneath by
 *             the flame.
 *             PREMULTIPLIED OVER (One, OneMinusSrcAlpha) carries ADDITIVE
 *             emission (α = 0) and OCCLUDING soot (α > 0) in ONE pass — the
 *             fix for the washed-out additive daylight flame. Set through
 *             CustomBlending, NOT `premultipliedAlpha = true`: three's
 *             NodeMaterial.setupOutput multiplies rgb by α when that flag is
 *             on, which would erase every α = 0 (pure emission) sample.
 *   'cards'   low, and every tier past 40 m: two Y-axis cylindrical
 *             billboards (a flame stays upright when you look down on it)
 *             with a noise-distorted teardrop mask, the same LUT, the same
 *             flicker and the same premultiplied blend.
 *   'sprite'  minimal/potato: three SpriteMaterial flames recoloured by the
 *             blackbody, scaled by the filtered flicker (the fire system).
 *   'sim'     ultra/WebGPU: lib/vfx/fire_sim.ts samples the shared compute
 *             grid through the same box and the same LUT.
 *
 * PER-FIRE STATE is per-OBJECT uniforms (`onObjectUpdate` reads the mesh's
 * userData before each draw), so every fire of a rung shares ONE material and
 * ONE pipeline. fog is OFF (a fogged premultiplied box would paint the fog
 * colour over the whole box); the fire system folds the fog's transmittance
 * into the exposure on the CPU instead. `markMrtOverlay`: the flame leaves the
 * G-buffer of the logs behind it alone (THE OVERLAY LAW).
 */
import * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, uint, texture, texture3D, positionWorld, positionView, positionGeometry, positionLocal,
  positionPrevious, cameraPosition, cameraNear, cameraFar, modelWorldMatrixInverse, screenCoordinate, frameId,
  Loop, If, max, min, smoothstep, saturate, fract, dot, cos, sin, abs, pow, exp, length, attribute, uv, select, mx_worley_noise_vec2, sqrt, modelWorldMatrix,
} from 'three/tsl';
import { vfxSceneLinearDepth } from './scene_depth.js';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { jitterNode } from 'enginex/render/bluenoise/SpatiotemporalBlueNoise.js'; // shader gaps R3 (agent F)
import { adaptiveSceneColor } from 'enginex/environment/water/adaptiveViewportTexture.js'; // surfaces (2026-09-27): THE HAZE COPY
import { blackbodyLut, BLACKBODY_LUT_SIZE, flameHex } from './blackbody.js';
import { bakeNoise3D, NOISE3D_SIZE, PeriodicNoise, periodicFbm, NOISE3D_GAIN } from './noise3d.js';
import { FIRE_SHADER, FIRE_DEFAULT_C, FIRE_DEFAULT_D, FIRE_FLIP, flipTexelCpu, flameEmissionCpu } from './fire_oracle.js';
export { FIRE_FLIP, flameEmissionCpu };

export { FIRE_SHADER };

/* eslint-disable @typescript-eslint/no-explicit-any */
type N = any;

// ── Shared textures ─────────────────────────────────────────────────────────
interface Tex { lut: THREE.DataTexture; noise: THREE.Data3DTexture; noiseData: Uint8Array; noiseReady: boolean; baking: boolean }
const G = globalThis as unknown as { __atlasFireTex?: Tex };

/** The LUT (HalfFloat RGBA 256×1: chroma + (T/Tmax)⁴) and the noise volume (RGBA8 64³, tiles). Created once. */
export function fireTextures(): Tex {
  if (G.__atlasFireTex) return G.__atlasFireTex;
  const lutF = blackbodyLut();
  const half = new Uint16Array(lutF.length);
  for (let i = 0; i < lutF.length; i++) half[i] = THREE.DataUtils.toHalfFloat(lutF[i]);
  const lut = new THREE.DataTexture(half, BLACKBODY_LUT_SIZE, 1, THREE.RGBAFormat, THREE.HalfFloatType);
  lut.colorSpace = THREE.NoColorSpace;
  lut.minFilter = THREE.LinearFilter; lut.magFilter = THREE.LinearFilter;
  lut.wrapS = lut.wrapT = THREE.ClampToEdgeWrapping;
  lut.generateMipmaps = false;
  lut.needsUpdate = true;
  lut.name = 'fire-blackbody-lut';
  const S = NOISE3D_SIZE;
  const data = new Uint8Array(S * S * S * 4).fill(128);
  const noise = new THREE.Data3DTexture(data, S, S, S);
  noise.format = THREE.RGBAFormat;
  noise.type = THREE.UnsignedByteType;
  noise.colorSpace = THREE.NoColorSpace;
  noise.minFilter = THREE.LinearFilter; noise.magFilter = THREE.LinearFilter;
  noise.wrapS = noise.wrapT = noise.wrapR = THREE.RepeatWrapping;
  noise.generateMipmaps = false;
  noise.unpackAlignment = 1;
  noise.needsUpdate = true;
  noise.name = 'fire-noise-64';
  G.__atlasFireTex = { lut, noise, noiseData: data, noiseReady: false, baking: false };
  return G.__atlasFireTex;
}

/**
 * Bake the noise volume in slices across frames (the whole bake is ~0.3 s of
 * main-thread work — sliced, it never hitches a frame). Resolves when the
 * texture holds the real noise. Idempotent.
 */
export function bakeFireNoise(slicesPerYield = 8): Promise<void> {
  const t = fireTextures();
  if (t.noiseReady || t.baking) return t.noiseReady ? Promise.resolve() : waitFor(() => t.noiseReady);
  t.baking = true;
  const S = NOISE3D_SIZE, seed = 0x5eed;
  const nr = new PeriodicNoise(seed), ng = new PeriodicNoise(seed + 101), nb = new PeriodicNoise(seed + 202), na = new PeriodicNoise(seed + 303);
  const low = [4, 8, 16, 32], high = [16, 32];
  const q = (v: number): number => Math.max(0, Math.min(255, Math.round(127.5 + v * 127.5 * NOISE3D_GAIN)));
  let z = 0;
  return new Promise<void>((resolve) => {
    const step = (): void => {
      const end = Math.min(S, z + slicesPerYield);
      for (; z < end; z++) {
        let o = z * S * S * 4;
        for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
          t.noiseData[o++] = q(periodicFbm(nr, x, y, z, S, low));
          t.noiseData[o++] = q(periodicFbm(ng, x, y, z, S, low));
          t.noiseData[o++] = q(periodicFbm(nb, x, y, z, S, low));
          t.noiseData[o++] = q(periodicFbm(na, x, y, z, S, high));
        }
      }
      if (z < S) { setTimeout(step, 0); return; }
      t.noise.needsUpdate = true;
      t.noiseReady = true;
      t.baking = false;
      resolve();
    };
    step();
  });
}
function waitFor(ok: () => boolean): Promise<void> {
  return new Promise((res) => { const tick = (): void => { if (ok()) res(); else setTimeout(tick, 50); }; tick(); });
}
/** The synchronous bake (the harness's oracle uses the same bytes). */
export function fireNoiseBytes(): Uint8Array { return bakeNoise3D(NOISE3D_SIZE, 0x5eed); }

// ── Global uniforms every fire reads ────────────────────────────────────────
/**
 * The light the SOOT scatters: the sky ambient + a share of the sun (written by the fire system per frame — on the
 * physical curve: (key · 0.3 + sky) / π in scene units, lib/photometry's rule 3).
 */
export const uSmokeLight = uniform(new THREE.Vector3(0.18, 0.2, 0.24));
/**
 * THE FIRE'S RADIANCE SCALE (session 3, 2026-09-26): every fire emission here — the flame, the sim, the cards, the
 * coal bed, the flame's glow on its own soot — is authored in the shaders' own units (the oracle's; the bed renders
 * them as they are) and multiplied by this. The fire system writes `fireRadianceScale(exposureState)` each frame
 * (lib/vfx/fire_rules.ts: a physical 10 kcd/m² core, held on screen once the display runs out of room for its
 * colour); nothing else writes it, so the bed and the harness keep 1. The fireball (an explosion) stays in its own units.
 */
export const uFireEmission = uniform(1);
/**
 * LOCAL VOLUMES (shader gaps program round 3, agent F, 2026-09-30): the box march's per-pixel start jitter — 0 = the
 * interleaved-gradient noise stepped by the golden ratio each frame (the fire's own since R1; three's volume-fire
 * example uses the same), 1 = the spatiotemporal blue-noise mask (engine bluenoise/SpatiotemporalBlueNoise, effect 2 —
 * never the light volume's sequence). AtlasVolumetricLightModule writes it from `volumeNoise` ('stbn' → 1).
 */
export const uFireJitterStbn = uniform(0);
/**
 * §4.3 (surfaces, 2026-09-27): the explosion's FLASH front (the shock ring's incandescent edge) on the curve —
 * `emitterScene(EMITTER_NITS.blast, EMITTER_DISPLAY_MAX.blast) / BLAST_RING_REF`, written by the fire system each frame
 * (1 = the shaders' own units: the bed, the harness, `__fire().physical(false)`).
 */
export const uBlastEmission = uniform(1);
/**
 * §4.5 THE BLUE BASE (surfaces, 2026-09-27; P3): a wood flame's base is a thin blue zone — CH* / C₂ chemiluminescence
 * (~430 / 470 nm) under the soot, 2–5 cm tall, only visible when the eye is dark-adapted. The volume rung's march adds a
 * dim blue stop over the lowest FIRE_BLUE_BAND of the flame's height, × this uniform (the fire system writes the night,
 * 0 by day; 0 = off — the bed, the harness, `physical(false)`).
 */
export const uFireBlue = uniform(0);
export const FIRE_BLUE_BAND = 0.05;
export const FIRE_BLUE_RGB: readonly [number, number, number] = [0.16, 0.3, 1.0];
/** The blue stop's strength against the flame's own emission per metre (CH* is faint beside the soot's glow). */
export const FIRE_BLUE_K = 0.06;
/** The shock ring's hot front's luminance in its own units (lum709 of vec3(6.0, 3.2, 1.2) — explosion_fx.ts). */
export const BLAST_RING_REF = 0.2126 * 6.0 + 0.7152 * 3.2 + 0.0722 * 1.2;

/**
 * THE HAZE COPY (surfaces agent, 2026-09-27 — the coordinator's WebGL2 `glCopyTexSubImage2D: Invalid copy texture format
 * combination`): every heat-haze quad (the fires, the blast disc, the hovercraft) samples the scene through a MID-PASS
 * COPY. three r185's `viewportSharedTexture` types its copy destination from the render target that is PUBLIC when the
 * texture is first ALLOCATED (Textures.updateTexture: a FramebufferTexture takes getRenderTarget()'s type, else RGBA8) —
 * allocated outside the RGBA16F scene pass (a pre-warm, a compile) it is RGBA8 for good, and ANGLE refuses every later
 * float → unorm copy. The water's `adaptiveSceneColor` keys the destination per EFFECTIVE target and re-flags it on a
 * type change. `localStorage 'atlas:haze-copy' = 'shared'` restores three's node (the repro lever).
 */
export function hazeSceneColor(): ((uvNode: never) => unknown) | undefined {
  let shared = false;
  try { shared = localStorage.getItem('atlas:haze-copy') === 'shared'; } catch { shared = false; }
  return shared ? undefined : (uvNode: never) => adaptiveSceneColor(uvNode);
}

/**
 * Per-object uniforms: fireA = (seed, exposure incl. flicker + fog + the kind's gain, heat, time s), fireB = (leanX,
 * leanZ, flame height, soot), fireC = (card yaw, base radius, puff phase, puff amplitude), fireD = (tongue frequency,
 * raggedness, pinch, sway). A mesh that carries no fireC / fireD (a bed, an old caller) draws a campfire's shape.
 */
function objVec4(key: 'fireA' | 'fireB' | 'fireC' | 'fireD'): N {
  const fallback = key === 'fireC' ? new THREE.Vector4(...FIRE_DEFAULT_C) : key === 'fireD' ? new THREE.Vector4(...FIRE_DEFAULT_D) : new THREE.Vector4();
  const read = (frame: { object: THREE.Object3D | null }): THREE.Vector4 => (frame.object?.userData[key] as THREE.Vector4 | undefined) ?? fallback;
  // three's typings declare a void callback; the UniformNode implementation assigns the returned value.
  return uniform(new THREE.Vector4()).onObjectUpdate(read as never);
}

// ── Shared density model (TSL) ──────────────────────────────────────────────
/** Noise fetch (vec4 0..1) from the shared volume. */
function noiseAt(uvw: N): N { return texture3D(fireTextures().noise, uvw); }

/**
 * The flame at a box-local point p (±0.5): returns vec4(emission rgb (premult per metre), extinction σ). The model and
 * its reasons are in lib/vfx/fire_oracle.ts (its header; `flameSampleCpu` is this function line for line — change both).
 * A = (seed, exposure, heat, t) · B = (leanX, leanZ, flame height, soot) · C = (card yaw, base radius, puff phase,
 * puff amplitude) · D = (tongue frequency, raggedness, pinch, sway).
 */
const flameSampleB = Fn(([p, A, B, C, D, thrBias]: [N, N, N, N, N, N]) => {
  const F = FIRE_SHADER;
  const seed = A.x, heat = A.z, t = A.w;
  const hF = max(B.z, float(0.2));
  const y01 = p.y.add(0.5);
  const h = y01.div(hF), hc = saturate(h);
  // THE RISING COORDINATE: √(height) — a feature ACCELERATES up the flame (u ∝ √z) and stretches as it climbs.
  const rise = sqrt(y01.mul(2.3).max(0).add(F.riseY0)).mul(F.riseK).sub(t.mul(F.riseW));
  // Fetch 1 — the sway: a slow, large warp that grows with height (the flame's line wanders, its root does not).
  const w = noiseAt(vec3(p.x.mul(0.7).add(seed), rise.mul(0.45).add(t.mul(0.09)).add(seed.mul(0.61)), p.z.mul(0.7).add(seed.mul(0.37)))).xyz.sub(0.5).mul(2);
  const sw = float(0.035).add(hc.mul(0.115)).mul(D.w);
  const qx = p.x.add(w.x.mul(sw)), qz = p.z.add(w.z.mul(sw));
  // The envelope: a (pinched) base, a belly, a closing top — and THE PUFF, a bulge travelling up it.
  const b0 = float(1).sub(D.z.mul(0.65)), hb = float(0.18).add(D.z.mul(0.4));
  const env = b0.add(float(1).sub(b0).mul(smoothstep(0, hb, hc))).mul(pow(max(float(1).sub(pow(hc, float(F.envE))), float(0)), float(F.envG)));
  const puff = float(1).add(C.w.mul(sin(C.z.sub(h.mul(0.9)).mul(6.2831853))));
  const R = C.y.mul(env).mul(puff);
  // Lean with the wind, growing with height².
  const lean = vec2(B.x, B.y).mul(h.mul(h));
  const rr = length(vec2(qx, qz).sub(lean));
  const r = rr.div(max(R, float(0.012)));
  const body = float(1).sub(smoothstep(0.55, 1.0, r));
  // Fetch 2 — the TONGUES: the four-octave channel (never the fine one alone: that was the speckle) against a threshold
  // that climbs with height and toward the edge: whole at the base, tongues above it, flamelets at the top.
  const n4 = noiseAt(vec3(qx.mul(1.3).mul(D.x).add(seed.mul(0.37)).add(t.mul(0.031)), rise.add(seed.mul(0.23)), qz.mul(1.3).mul(D.x).add(seed.mul(0.71)).sub(t.mul(0.023))));
  const n = n4.x.sub(0.5).mul(2).add(n4.w.sub(0.5).mul(2 * F.fine));
  const n2 = n4.y.sub(0.5).mul(2);
  // FEWER TONGUES = A MORE SOLID FLAME (the oracle's thrTongue): a torch holds together to its tip.
  const thr = float(F.thr0).add(pow(hc, float(1.1)).mul(D.x.sub(1.2).mul(F.thrTongue).add(F.thr1 - F.thr0))).add(D.y.mul(0.5).mul(r).mul(r)).add(thrBias.mul(float(1).sub(hc.mul(0.7))));
  const x = n.sub(thr);
  // SHEETS, not a solid: density peaks in a shell at the threshold and falls inside it.
  const shell = smoothstep(0, F.soft, x), inner = smoothstep(F.soft, F.soft + 0.5, x);
  // THE WALLS: nothing reaches a face of the box (a leaning plume was cut flat at the wall).
  const wall = saturate(float(0.5).sub(abs(p.x)).div(F.wallFade)).mul(saturate(float(0.5).sub(abs(p.z)).div(F.wallFade))).mul(saturate(float(0.5).sub(p.y).div(F.lidFade)));
  const dens = shell.mul(float(1).sub(inner.mul(F.hollow))).mul(body).mul(float(1).sub(smoothstep(1.0, 1.12, h))).mul(wall);
  // Temperature is a field of its own (hot low on the axis, cooler up and out) — never the density: a thin sheet is not a cold one.
  const rc = min(r, float(1.2));
  const temp = saturate(heat.mul(float(1).sub(pow(hc, float(1.3)).mul(0.26)).sub(rc.mul(rc).mul(0.22)).add(n2.mul(0.07))).mul(float(0.70).add(shell.mul(float(1).sub(inner.mul(0.7))).mul(0.30))));
  const bb = texture(fireTextures().lut, vec2(temp, 0.5));
  // The CORE: the thick lower middle burns brightest (the cream heart of an orange flame).
  const core = float(1).sub(smoothstep(0, 0.6, r)).mul(float(1).sub(smoothstep(0.1, 0.75, hc)));
  let emis = bb.xyz.mul(bb.w).mul(pow(dens, float(F.densPow))).mul(float(1).add(core.mul(F.coreK))).mul(float(F.emissionPerM));
  // §4.5 (surfaces, 2026-09-27): the blue base — the lowest FIRE_BLUE_BAND of the flame's height, inside its shape, at night.
  // (fire realism, 2026-10-08) It burns only where a flame sheet is — it used a density floor of 0.35 and drew a blue
  // disc under the whole envelope: a gas ring. On 97 photographs the blue at a wood fire's base is under 0.3 % of it.
  const blueBand = float(1).sub(smoothstep(0.0, FIRE_BLUE_BAND, h)).mul(body).mul(wall).mul(uFireBlue);
  emis = emis.add(vec3(FIRE_BLUE_RGB[0], FIRE_BLUE_RGB[1], FIRE_BLUE_RGB[2]).mul(blueBand).mul(shell).mul(F.emissionPerM * FIRE_BLUE_K));
  // SOOT: the dark breath at the tips (never inside the flame) — a SHORT plume, eaten by the same noise. The smoke
  // column above it is the particles' (or the smoke cards'): drawn here it was a straight grey streak to the box's lid.
  const cool = float(1).sub(smoothstep(0.3, 0.6, temp));
  const plume = smoothstep(hF.mul(0.82), hF.mul(1.1), y01).mul(float(1).sub(smoothstep(hF.mul(1.25), hF.mul(1.75), y01)));
  const plumeR = float(0.10).add(y01.sub(hF.mul(0.8)).max(0).mul(0.34));
  const plumeShape = float(1).sub(smoothstep(0.4, 1.0, rr.div(plumeR)));
  const sootDens = saturate(plume.mul(plumeShape).mul(n.mul(0.9).add(0.45))).mul(B.w).mul(wall);
  const sigma = dens.mul(float(F.sigmaFlame).add(cool.mul(F.sigmaSoot * 0.12))).add(sootDens.mul(F.sigmaSoot * 0.5));
  // Soot is lit: the sky, and the flame from beneath — at ~1 % of the core (the measured halo over a flame at night;
  // the old 0.9 lit the smoke a fifth as bright as the flame under it) — the flame's own light, so it wears its scale.
  const under = exp(y01.sub(hF).max(0).mul(-4.5)).mul(heat).mul(F.underGlow).mul(uFireEmission);
  const sootLit = uSmokeLight.add(vec3(1.0, 0.42, 0.12).mul(under));
  const scat = sootLit.mul(sootDens.add(dens.mul(cool).mul(0.12))).mul(F.sigmaSoot * 0.5 * F.sootAlbedo);
  return vec4(emis.mul(A.y).mul(uFireEmission).add(scat), sigma);
});
/** The volume's flame: no threshold bias (a card's SLICE takes one — fireCardMaterial). */
const flameSample = (p: N, A: N, B: N, C: N, D: N): N => flameSampleB(p, A, B, C, D, float(0));

/**
 * A FIREBALL (R5) in its unit box: a warped ball whose temperature falls with
 * `heat` while soot rises — it COOLS INTO SMOKE in the same shader.
 * A = (seed, exposure, heat, t), B = (unused, unused, radius 0..1, soot 0..1).
 */
const fireballSample = Fn(([p, A, B]: [N, N, N]) => {
  const seed = A.x, heat = A.z, t = A.w;
  const w = noiseAt(p.mul(0.9).add(vec3(seed, seed.mul(0.5), t.mul(0.12)))).xyz.sub(0.5).mul(2);
  const pw = p.add(w.mul(0.09));
  const rho = length(pw).div(0.5);
  const radius = max(B.z, float(0.05));
  const n = noiseAt(pw.mul(1.6).add(vec3(seed.mul(0.3), t.mul(-0.25), seed.mul(0.7)))).w;
  const dens = saturate(float(1).sub(rho.div(radius)).mul(3.2).add(n.sub(0.5).mul(1.5)));
  const temp = saturate(dens.mul(heat).mul(float(1.35).sub(rho.mul(0.6))));
  const bb = texture(fireTextures().lut, vec2(temp, 0.5));
  // Gated by temperature (the sim rung's rule): the LUT's floor is 800 K — a dull red at a = 1/16 — so without the
  // gate a fully COOLED ball (heat 0) still glowed red over its grey soot and read as a flat pink disc (the bed, u 0.8).
  const emis = bb.xyz.mul(bb.w).mul(dens).mul(smoothstep(0.04, 0.22, temp)).mul(float(FIRE_SHADER.emissionPerM));
  const cool = float(1).sub(smoothstep(0.1, 0.5, temp));
  const soot = B.w;
  const sigma = dens.mul(float(1.2).add(cool.mul(soot).mul(FIRE_SHADER.sigmaSoot)));
  const sootLit = uSmokeLight.add(vec3(1.0, 0.45, 0.15).mul(heat.mul(0.8)));
  const scat = sootLit.mul(dens.mul(cool).mul(soot)).mul(FIRE_SHADER.sigmaSoot * FIRE_SHADER.sootAlbedo);
  return vec4(emis.mul(A.y).add(scat), sigma);
});

// ── The box march ───────────────────────────────────────────────────────────
function marchMaterial(kind: 'flame' | 'fireball' | 'sim', steps: number, simSample?: N): THREE.NodeMaterial {
  const A = objVec4('fireA'), B = objVec4('fireB'), C4 = objVec4('fireC'), D4 = objVec4('fireD');
  const sampleFn = kind === 'flame' ? ((p: N, a: N, b: N) => flameSample(p, a, b, C4, D4)) : kind === 'sim' ? simSample : fireballSample;
  const out = Fn(() => {
    const camL = modelWorldMatrixInverse.mul(vec4(cameraPosition, 1)).xyz;
    const toFrag = positionWorld.sub(cameraPosition);
    const fragDist = length(toFrag);
    const dirW = toFrag.div(max(fragDist, float(1e-5)));
    const dirL = modelWorldMatrixInverse.mul(vec4(dirW, 0)).xyz; // box-local units per WORLD metre
    // Slab test against the unit box: t is in world metres along the view ray.
    const inv = vec3(1).div(dirL.add(vec3(1e-7)));
    const t0 = vec3(-0.5).sub(camL).mul(inv), t1 = vec3(0.5).sub(camL).mul(inv);
    const tn = min(t0, t1), tf = max(t0, t1);
    const tEnter = max(max(max(tn.x, tn.y), tn.z), float(0));
    const tExit = min(min(tf.x, tf.y), tf.z);
    // The opaque depth behind this pixel, as a distance along the ray.
    // THE VFX SNAPSHOT (scene_depth.ts): with its depth test off, THIS read is the flame's whole occlusion — it must be
    // the depth of everything opaque, never a snapshot an opaque reader (the water) took half-way through the opaques.
    const sceneDepth = vfxSceneLinearDepth.mul(cameraFar.sub(cameraNear)).add(cameraNear);
    const fragDepth = positionView.z.negate().max(1e-4);
    const tOpaque = sceneDepth.mul(fragDist.div(fragDepth));
    const tEnd = min(tExit, tOpaque);
    const C = vec3(0).toVar('fireC');
    const T = float(1).toVar('fireT');
    If(tEnd.greaterThan(tEnter), () => {
      const ds = tEnd.sub(tEnter).div(float(steps));
      // Interleaved-gradient jitter, golden-ratio-stepped per frame: 12–16 steps never band.
      const ign = fract(float(52.9829189).mul(fract(dot(screenCoordinate.xy, vec2(0.06711056, 0.00583715)))));
      const jitIgn = fract(ign.add(float(frameId).mul(0.61803398875))).toVar();
      // R3: or the STBN sequence — sampled before the select (THE LAZY-SAMPLE LAW).
      const jitStbn = float(jitterNode('stbn', 2)).toVar();
      const jit = select(uFireJitterStbn.greaterThan(0.5), jitStbn, jitIgn);
      const t = tEnter.add(ds.mul(jit)).toVar('fireTs');
      Loop({ start: uint(0), end: uint(steps), type: 'uint' }, () => {
        If(T.greaterThan(0.015).and(t.lessThan(tEnd)), () => {
          const p = camL.add(dirL.mul(t));
          const s = sampleFn(p, A, B);
          const sigma = s.w;
          // The exact segment integral for emission + in-scatter: ΔC = T·q·(1 − e^{−σ ds})/σ → T·q·ds as σ → 0.
          const att = exp(sigma.mul(ds).negate());
          const w = select(sigma.greaterThan(1e-4), float(1).sub(att).div(sigma), ds);
          C.addAssign(s.xyz.mul(T).mul(w));
          T.mulAssign(att);
          t.addAssign(ds);
        });
      });
    });
    return vec4(C, float(1).sub(T));
  });
  const m = new THREE.NodeMaterial();
  m.name = kind === 'flame' ? `fire-volume-${steps}` : kind === 'sim' ? `fire-sim-${steps}` : `fireball-${steps}`;
  m.side = THREE.BackSide;
  m.transparent = true;
  m.depthWrite = false;
  m.depthTest = false; // the march clamps at the opaque depth itself (see the header)
  m.fog = false;
  m.blending = THREE.CustomBlending;
  m.blendSrc = THREE.OneFactor;
  m.blendDst = THREE.OneMinusSrcAlphaFactor;
  m.blendSrcAlpha = THREE.OneFactor;
  m.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
  m.colorNode = out();
  // A static box: pin the previous position (the positionPrevious law) — nothing here moves per vertex.
  m.positionNode = Fn(() => { positionPrevious.assign(positionLocal); return positionLocal; })();
  markMrtOverlay(m);
  return m;
}

const mats = new Map<string, THREE.NodeMaterial>();
/** The volume rung's material for a step count (cached: one pipeline per step count). */
export function fireVolumeMaterial(steps: number): THREE.NodeMaterial {
  const k = `v${steps}`;
  let m = mats.get(k);
  if (!m) { m = marchMaterial('flame', steps); mats.set(k, m); }
  return m;
}
/**
 * The 'sim' rung's material: the same march, sampling the SHARED compute grid (lib/vfx/fire_sim.ts) through this
 * fire's box — rotated about Y and mirrored by the fire's seed so two hero fires never read as twins. The grid's
 * R = smoke density, G = temperature (≈ 0..4 near the bed): temperature → the blackbody LUT, density → emission
 * weight and soot. `dyeNode` is the sim's render-side texture node (its value follows the ping-pong).
 */
export function fireSimMaterial(steps: number, dyeNode: N): THREE.NodeMaterial {
  const k = `s${steps}`;
  let m = mats.get(k);
  if (m) return m;
  const simC = objVec4('fireC');
  const F = FIRE_SHADER;
  const simSample = Fn(([p, A, B]: [N, N, N]) => {
    const seed = A.x, heat = A.z;
    const ang = seed.mul(6.2831853);
    const c = cos(ang), sn = sin(ang);
    const mirror = select(fract(seed.mul(7.13)).greaterThan(0.5), float(-1), float(1));
    const rx = p.x.mul(c).sub(p.z.mul(sn)).mul(mirror), rz = p.x.mul(sn).add(p.z.mul(c));
    const yy = p.y.add(0.5);
    const lean = vec2(B.x, B.y).mul(yy.mul(yy));
    const uvw = vec3(rx.sub(lean.x), p.y, rz.sub(lean.y)).add(0.5);
    // Sample THROUGH the sim's node: its value follows the ping-pong (a fresh texture3D(value) would pin one buffer).
    const dye = dyeNode.sample(uvw).level(0);
    // THE WALLS (fire realism, 2026-10-08): the grid's gas reaches the box's faces and was cut flat there — the known
    // "plume clips at its box walls". Fade it out over the last tenth, the lid over the last eighth.
    const simWall = saturate(float(0.5).sub(abs(p.x)).div(0.1)).mul(saturate(float(0.5).sub(abs(p.z)).div(0.1))).mul(saturate(float(0.5).sub(p.y).div(0.13)));
    const density = saturate(dye.x.mul(0.45)).mul(simWall);
    // THE FLAME ENDS WHERE THE KIND'S FLAME ENDS (fire realism): the grid's gas kept a third of its heat to the lid and
    // the whole box glowed as a pillar (height ÷ width 2.9–3.5 against a campfire's 1.0–1.5). Above the kind's flame
    // height (fireB.z, the envelope every other rung draws) the gas is smoke: its heat reads a quarter.
    const hF = B.z.max(0.2);
    const hh = yy.div(hF);
    const top = float(1).sub(smoothstep(0.6, 1.1, hh));
    // … and outside the kind's envelope (the flame necks in as it rises; the grid's column stood as wide as its bed
    // all the way up): the same radius law the volume draws, read off fireC.y.
    const Rs = simC.y.mul(pow(float(1).sub(pow(saturate(hh), float(F.envE))).max(0), float(F.envG))).add(0.06);
    const inEnv = float(1).sub(smoothstep(0.55, 1.25, length(vec2(uvw.x.sub(0.5), uvw.z.sub(0.5))).div(Rs)));
    // the grid's heat is flat and low through the column (it drew one dull salmon): the square root spreads it
    const temp = sqrt(saturate(dye.y.div(3.5))).mul(heat).mul(top).mul(float(0.3).add(inEnv.mul(0.7)));
    const bb = texture(fireTextures().lut, vec2(temp, 0.5));
    // × simGain: the grid's core matches the volume's (fire_oracle); × uFireEmission: on the curve.
    // the volume's hot heart (coreK): low and on the axis — the grid's own picture has no brighter middle
    const simCore = float(1).sub(smoothstep(0.0, 0.6, length(vec2(uvw.x.sub(0.5), uvw.z.sub(0.5))).div(Rs))).mul(float(1).sub(smoothstep(0.1, 0.75, saturate(hh))));
    const emis = bb.xyz.mul(bb.w).mul(density).mul(smoothstep(0.08, 0.3, temp)).mul(float(1).add(simCore.mul(F.coreK))).mul(float(FIRE_SHADER.emissionPerM * FIRE_SHADER.simGain));
    const cool = float(1).sub(smoothstep(0.08, 0.42, temp));
    const sigma = density.mul(float(FIRE_SHADER.sigmaFlame).add(cool.mul(FIRE_SHADER.sigmaSoot * 0.5)));
    const scat = uSmokeLight.add(vec3(1.0, 0.42, 0.12).mul(heat).mul(exp(p.y.add(0.5).mul(-3))).mul(uFireEmission)).mul(density.mul(cool)).mul(FIRE_SHADER.sigmaSoot * 0.5 * FIRE_SHADER.sootAlbedo);
    return vec4(emis.mul(A.y).mul(uFireEmission).add(scat), sigma);
  });
  m = marchMaterial('sim', steps, simSample);
  mats.set(k, m);
  return m;
}

/** The fireball's material (R5). */
export function fireballMaterial(steps: number): THREE.NodeMaterial {
  const k = `b${steps}`;
  let m = mats.get(k);
  if (!m) { m = marchMaterial('fireball', steps); mats.set(k, m); }
  return m;
}

let boxGeo: THREE.BoxGeometry | null = null;
/** The shared unit box (position, normal, uv — three vertex buffers). */
export function fireBoxGeometry(): THREE.BoxGeometry {
  if (!boxGeo) { boxGeo = new THREE.BoxGeometry(1, 1, 1); boxGeo.userData.shared = true; }
  return boxGeo;
}

// ── The cards rung ──────────────────────────────────────────────────────────
let cardGeo: THREE.BufferGeometry | null = null;
/** Two unit cards (x ±0.5, y 0..1) — `aCard` tells them apart; four vertex buffers. */
export function fireCardGeometry(): THREE.BufferGeometry {
  if (cardGeo) return cardGeo;
  const pos: number[] = [], nrm: number[] = [], uvs: number[] = [], card: number[] = [], idx: number[] = [];
  for (let c = 0; c < 2; c++) {
    const b = c * 4;
    for (const [x, y] of [[-0.5, 0], [0.5, 0], [0.5, 1], [-0.5, 1]] as const) { pos.push(x, y, 0); nrm.push(0, 0, 1); uvs.push(x + 0.5, y); card.push(c); }
    idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setAttribute('aCard', new THREE.Float32BufferAttribute(card, 1));
  g.setIndex(idx);
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0.5, 0), 1.2);
  g.userData.shared = true;
  cardGeo = g;
  return g;
}

let cardMat: THREE.NodeMaterial | null = null;
/**
 * The cards (fire realism, 2026-10-08): TWO SLICES OF THE SAME FLAME. Each card evaluates the volume rung's own
 * `flameSample` once, on a vertical plane through the fire's box (the card spans the whole box: x ±0.5, y 0..1), and
 * stands for a slab of flame `FIRE_SHADER.cardSlab` of the box thick — so the cards rung has the volume's shape, tongues,
 * colour, puff and soot by construction (the first cards drew their own teardrop: a different, smaller fire at 40 m).
 * Per-object fireA / fireB / fireD as the volume's; fireC.x = the camera-facing yaw (the fire system writes it per frame;
 * a card turns about Y only, so a flame stays upright when seen from above).
 */
export function fireCardMaterial(): THREE.NodeMaterial {
  if (cardMat) return cardMat;
  const A = objVec4('fireA'), B = objVec4('fireB'), Cc = objVec4('fireC'), Dd = objVec4('fireD');
  const aCard = attribute('aCard', 'float');
  const m = new THREE.NodeMaterial();
  m.name = 'fire-cards';
  m.side = THREE.DoubleSide;
  m.transparent = true;
  m.depthWrite = false;
  m.fog = false;
  m.blending = THREE.CustomBlending;
  m.blendSrc = THREE.OneFactor;
  m.blendDst = THREE.OneMinusSrcAlphaFactor;
  m.blendSrcAlpha = THREE.OneFactor;
  m.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
  m.positionNode = Fn(() => {
    // The card's yaw faces the camera (±0.45 rad apart), measured in GEOMETRY space (the positionGeometry law).
    const yaw = Cc.x.add(select(aCard.greaterThan(0.5), float(0.45), float(-0.45)));
    const p = vec3(positionGeometry.x.mul(cos(yaw)), positionGeometry.y, positionGeometry.x.mul(sin(yaw)).negate());
    positionPrevious.assign(p); // a turning card is not motion (the positionPrevious law)
    return p;
  })();
  m.colorNode = Fn(() => {
    const u0 = uv();
    // The slice: the card's own plane through the box, the two cards a tenth of the box apart in depth (two slices of
    // one flame, not the same picture twice).
    const p = vec3(u0.x.sub(0.5), u0.y.sub(0.5), aCard.sub(0.5).mul(0.12));
    // A SLICE IS THINNER THAN A PROJECTION: the volume's picture is every tongue along the ray; one plane through it
    // shows only the tongues that cross the plane (the cards drew a sliver: height ÷ width 2.3 against the volume's 1.1).
    // The slice's tongue threshold is therefore lower everywhere (cardThrBias) and its tongues a fifth broader.
    const Ds = vec4(Dd.x.mul(0.8), Dd.y, Dd.z, Dd.w);
    const s = flameSampleB(p, A, B, Cc, Ds, float(FIRE_SHADER.cardThrBias));
    // the card's lower edge is not a flame's (two bright lines crossed on the ground under a low, wide fire)
    const foot = smoothstep(0.0, 0.05, u0.y);
    // Each card is half of a slab of flame cardSlab of the box wide, in WORLD metres (the mesh is scaled to the box).
    const boxW = length(modelWorldMatrix.mul(vec4(1, 0, 0, 0)).xyz);
    const slab = boxW.mul(FIRE_SHADER.cardSlab * 0.5 * FIRE_SHADER.cardGain);
    const alpha = float(1).sub(exp(s.w.mul(slab).negate()));
    // Soft against the ground and the logs.
    const sceneDepth = vfxSceneLinearDepth.mul(cameraFar.sub(cameraNear)).add(cameraNear);
    const fade = saturate(sceneDepth.sub(positionView.z.negate()).div(0.25));
    // the slab's own segment integral, as the march's: a thick flame absorbs its own light (a bonfire's cards blew out)
    const wSlab = select(s.w.greaterThan(1e-4), alpha.div(s.w.max(1e-4)), slab);
    return vec4(s.xyz.mul(wSlab).mul(fade).mul(foot), alpha.mul(fade).mul(foot));
  })();
  markMrtOverlay(m);
  cardMat = m;
  return m;
}

// ── The sprite rung ─────────────────────────────────────────────────────────
/** The sprite rung's own HDR gains (its tuning in the shaders' units — the bed's night-sprite shot). */
const SPRITE_FLAME_GAIN = 2.2, SPRITE_EMBER_GAIN = 2.5;
let spriteFlame: THREE.SpriteMaterial | null = null, spriteEmber: THREE.SpriteMaterial | null = null;
function canvasTex(draw: (g: CanvasRenderingContext2D, s: number) => void, size: number): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  draw(c.getContext('2d')!, size);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
const hexCss = (hex: number, a: number): string => `rgba(${(hex >> 16) & 255},${(hex >> 8) & 255},${hex & 255},${a})`;
/**
 * The minimal/potato flame (fire realism, 2026-10-08): a TONGUE, not a balloon — a pointed, slightly bent flame shape
 * with a hot heart low in it, in the LUT's own colours (the flame as the richer rungs draw it: cream heart, orange
 * body, red rim), HDR-scaled. Three of them, each on its own flicker and lean, make the fire (the fire system).
 */
export function fireSpriteMaterials(): { flame: THREE.SpriteMaterial; ember: THREE.SpriteMaterial } {
  if (!spriteFlame) {
    const tex = canvasTex((g, s) => {
      // the tongue: wide low, a pointed tip bent to one side
      const tongue = (): void => {
        g.beginPath();
        g.moveTo(s * 0.56, s * 0.02);
        g.bezierCurveTo(s * 0.60, s * 0.30, s * 0.86, s * 0.52, s * 0.80, s * 0.80);
        g.bezierCurveTo(s * 0.76, s * 0.97, s * 0.24, s * 0.97, s * 0.20, s * 0.80);
        g.bezierCurveTo(s * 0.14, s * 0.56, s * 0.42, s * 0.36, s * 0.56, s * 0.02);
        g.closePath();
      };
      g.filter = `blur(${Math.max(1, s / 40)}px)`;
      const body = g.createLinearGradient(0, s * 0.98, 0, 0);
      body.addColorStop(0, hexCss(flameHex(1500), 0.95));
      body.addColorStop(0.45, hexCss(flameHex(1300), 0.8));
      body.addColorStop(0.8, hexCss(flameHex(1050), 0.4));
      body.addColorStop(1, hexCss(flameHex(900), 0));
      g.fillStyle = body; tongue(); g.fill();
      // the heart
      const grd = g.createRadialGradient(s * 0.5, s * 0.74, 1, s * 0.5, s * 0.66, s * 0.3);
      grd.addColorStop(0, 'rgba(255,236,200,1)');
      grd.addColorStop(0.5, hexCss(flameHex(1600), 0.75));
      grd.addColorStop(1, 'rgba(0,0,0,0)');
      g.globalCompositeOperation = 'lighter';
      g.fillStyle = grd; tongue(); g.fill();
    }, 128);
    spriteFlame = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, color: new THREE.Color(SPRITE_FLAME_GAIN, SPRITE_FLAME_GAIN, SPRITE_FLAME_GAIN) });
    spriteFlame.name = 'fire-sprite';
    markMrtOverlay(spriteFlame); // THE OVERLAY LAW: a flame must not rewrite the G-buffer of the logs behind it
  }
  if (!spriteEmber) {
    const tex = canvasTex((g, s) => {
      const grd = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
      grd.addColorStop(0, 'rgba(255,230,160,1)');
      grd.addColorStop(0.4, hexCss(flameHex(1400), 0.8));
      grd.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = grd;
      g.fillRect(0, 0, s, s);
    }, 16);
    spriteEmber = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, color: new THREE.Color(SPRITE_EMBER_GAIN, SPRITE_EMBER_GAIN, SPRITE_EMBER_GAIN) });
    spriteEmber.name = 'fire-sprite-ember';
    markMrtOverlay(spriteEmber);
  }
  return { flame: spriteFlame, ember: spriteEmber };
}
/** Put the sprite rung on the curve: its colours × the fire's radiance scale (a no-op until the sprites exist). */
export function setFireSpriteScale(k: number): void {
  spriteFlame?.color.setScalar(SPRITE_FLAME_GAIN * k);
  spriteEmber?.color.setScalar(SPRITE_EMBER_GAIN * k);
  if (flip) for (const m of flip.mats) m.color.setScalar(FIRE_FLIP.ref * FIRE_FLIP.layerGain * k);
  flipScale = k;
}

// ── The sprite rung's FLIPBOOK (fire realism, 2026-10-08) ────────────────────
/**
 * THE LOW END DRAWS THE SAME FIRE. The minimal / potato flame was three painted teardrops — a different fire from the
 * one every other rung draws. It is now a FLIPBOOK OF THE ORACLE: `frames` pictures of the volume rung's own flame
 * (`marchFireCpu`, a campfire seen from the front: `spanX` of the box wide, `spanY` of it tall from the base), baked
 * once on the CPU a few rows a frame (never a hitch; the painted tongue stands in for the ~1 s the bake takes), played
 * at `fps` on the fire's own Froude clock. The loop is seamless by construction: frame k is the flame at t_k cross-faded
 * (k ÷ frames) into the flame one loop EARLIER, so the last frame leads into the first, and the puff makes exactly one
 * cycle a loop. A kind's own shape is the sprite's scale (fire_system: base radius → width, flame height → height).
 * `ref` is the shader-unit radiance a white texel stands for (the core's mean is 2.6, its peaks 5.3: above 4 clips).
 */
interface Flip { mats: THREE.SpriteMaterial[]; px: Uint8ClampedArray[]; frame: number; row: number; ready: boolean; lut: Float32Array }
let flip: Flip | null = null;
/** Kill switch: `localStorage atlas:fire-flipbook = 'off'` keeps the painted tongues (read once). */
let flipOff: boolean | null = null;
function flipbookOff(): boolean {
  if (flipOff === null) { try { flipOff = localStorage.getItem('atlas:fire-flipbook') === 'off'; } catch { flipOff = false; } }
  return flipOff;
}
let flipScale = 1;
const srgbByte = (v: number): number => { const c = v <= 0 ? 0 : v >= 1 ? 1 : v; return Math.round(255 * (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055)); };
/**
 * Advance the flipbook's bake by at most `budgetMs` (the fire system calls it while a sprite-rung fire lives). Returns
 * true once every frame is a material. Needs the noise volume (it starts that bake itself).
 */
export function stepFireFlipbook(budgetMs = 3): boolean {
  if (flipbookOff()) return false;
  if (flip?.ready) return true;
  const t = fireTextures();
  if (!t.noiseReady) { if (!t.baking) void bakeFireNoise(4); return false; }
  if (typeof document === 'undefined') return false;
  const Fp = FIRE_FLIP;
  if (!flip) flip = { mats: [], px: [], frame: 0, row: 0, ready: false, lut: blackbodyLut() };
  const t0 = performance.now();
  while (flip.frame < Fp.frames && performance.now() - t0 < budgetMs) {
    if (flip.row === 0) flip.px[flip.frame] = new Uint8ClampedArray(Fp.w * Fp.h * 4);
    const px = flip.px[flip.frame], iy = flip.row;
    const o0 = (Fp.h - 1 - iy) * Fp.w * 4; // canvas rows run top-down; the flame's base is the last row
    for (let ix = 0; ix < Fp.w; ix++) {
      const c = flipTexelCpu(t.noiseData, flip.lut, flip.frame, ix, iy);
      const o = o0 + ix * 4;
      px[o] = srgbByte(c[0] / Fp.ref); px[o + 1] = srgbByte(c[1] / Fp.ref); px[o + 2] = srgbByte(c[2] / Fp.ref); px[o + 3] = 255;
    }
    if (++flip.row >= Fp.h) {
      const cv = document.createElement('canvas');
      cv.width = Fp.w; cv.height = Fp.h;
      cv.getContext('2d')!.putImageData(new ImageData(px as unknown as Uint8ClampedArray<ArrayBuffer>, Fp.w, Fp.h), 0, 0);
      const tex = new THREE.CanvasTexture(cv);
      tex.colorSpace = THREE.SRGBColorSpace;
      // additive: black is "no flame" (alpha is 1 everywhere — AdditiveBlending multiplies by it)
      const m = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, color: new THREE.Color().setScalar(Fp.ref * Fp.layerGain * flipScale) });
      m.name = 'fire-sprite';
      markMrtOverlay(m); // THE OVERLAY LAW
      flip.mats.push(m);
      flip.row = 0; flip.frame++;
    }
  }
  if (flip.frame >= Fp.frames) { flip.ready = true; flip.px = []; }
  return flip.ready;
}
/** The flipbook's frames (null until the bake is done). */
export function fireFlipbookMaterials(): THREE.SpriteMaterial[] | null { return flip?.ready ? flip.mats : null; }

// ── The smoke column on WebGL 2 / low (R4): cylindrical smoke cards ────────
let smokeGeo: THREE.BufferGeometry | null = null;
/** Two tall unit cards (x ±0.5, y 0..1), `aCard` 0/1 — the second a little wider and phase-shifted. */
export function fireSmokeCardGeometry(): THREE.BufferGeometry {
  if (!smokeGeo) { smokeGeo = fireCardGeometry().clone(); smokeGeo.userData.shared = true; }
  return smokeGeo;
}
let smokeCardMat: THREE.NodeMaterial | null = null;
/**
 * The Bad Company 2 column recipe: a Y-axis billboard whose mask widens with
 * height, eaten by a scrolling noise so it billows upward, tinted by the
 * sky light the six-way smoke uses (`uSmokeLight`), lit orange from beneath
 * by the fire, and soft-faded where it meets geometry. Premultiplied OVER.
 * fireA = (seed, glow, heat, t), fireB.w = density, fireC.x = yaw.
 */
export function fireSmokeCardMaterial(): THREE.NodeMaterial {
  if (smokeCardMat) return smokeCardMat;
  const A = objVec4('fireA'), B = objVec4('fireB'), Cc = objVec4('fireC');
  const aCard = attribute('aCard', 'float');
  const m = new THREE.NodeMaterial();
  m.name = 'fire-smoke-cards';
  m.side = THREE.DoubleSide;
  m.transparent = true;
  m.depthWrite = false;
  m.fog = false;
  m.blending = THREE.CustomBlending;
  m.blendSrc = THREE.OneFactor;
  m.blendDst = THREE.OneMinusSrcAlphaFactor;
  m.blendSrcAlpha = THREE.OneFactor;
  m.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
  m.positionNode = Fn(() => {
    const yaw = Cc.x.add(select(aCard.greaterThan(0.5), float(0.7), float(0)));
    const wid = select(aCard.greaterThan(0.5), float(1.25), float(1));
    const p = vec3(positionGeometry.x.mul(wid).mul(cos(yaw)), positionGeometry.y, positionGeometry.x.mul(wid).mul(sin(yaw)).negate());
    positionPrevious.assign(p);
    return p;
  })();
  m.colorNode = Fn(() => {
    const seed = A.x, glow = A.y, heat = A.z, t = A.w;
    const u0 = uv();
    const x = u0.x.sub(0.5).mul(2), y = u0.y;
    // The column widens as it rises, bends with the wind (B.x), billows upward.
    const lean = B.x.mul(y.mul(y)).mul(0.5);
    const halfW = float(0.28).add(y.mul(0.6));
    const w = noiseAt(vec3(x.mul(0.25).add(seed).add(aCard.mul(0.41)), y.mul(0.35).sub(t.mul(0.09)), seed.mul(0.7).add(t.mul(0.02)))).xyz.sub(0.5).mul(2);
    const xr = abs(x.sub(lean).add(w.x.mul(0.25))).div(halfW);
    const n = noiseAt(vec3(x.mul(0.45).add(seed.mul(1.7)), y.mul(0.6).sub(t.mul(0.12)), t.mul(0.03).add(aCard.mul(0.5)))).w;
    const body = float(1).sub(smoothstep(0.35, 1.0, xr)).mul(smoothstep(0.0, 0.12, y)).mul(float(1).sub(smoothstep(0.6, 1.0, y)));
    const dens = saturate(body.mul(n.mul(1.8).sub(0.45).add(w.y.mul(0.2)))).mul(B.w);
    const alpha = saturate(dens.mul(0.55));
    // (fire realism, 2026-10-08) × underGlow: measured on night photographs the air over a fire is ~1 % of the flame's
    // luminance; unscaled, the WebGL 2 column glowed tan at a third of the core's and stood over every fire like a second flame
    // (98 × the dark beside it against the photographs' 6.7).
    const under = exp(y.mul(-7)).mul(heat).mul(glow).mul(uFireEmission).mul(FIRE_SHADER.underGlow); // the fire's own light: its radiance scale
    const lit = uSmokeLight.mul(0.95).add(vec3(1.0, 0.45, 0.14).mul(under));
    const sceneDepth = vfxSceneLinearDepth.mul(cameraFar.sub(cameraNear)).add(cameraNear);
    const fade = saturate(sceneDepth.sub(positionView.z.negate()).div(0.6));
    const a = alpha.mul(fade);
    return vec4(lit.mul(a), a);
  })();
  markMrtOverlay(m);
  smokeCardMat = m;
  return m;
}

// ── The fuel bed (R2): the ring's ember cylinder, glowing ─────────────────
let bedMat: THREE.MeshStandardNodeMaterial | null = null;
/**
 * The laid fire's bed of coals: a lit, near-black char surface whose CRACKS
 * glow — Worley cells are the coal chunks, the F2 − F1 ridge between them the
 * seam of live heat — coloured by the same blackbody LUT (≈1,000–1,250 K)
 * and breathing with the fire's flicker (per-object fireA.y, the exposure the
 * flame itself wears). The cells are measured in GEOMETRY space, offset +40
 * so the lattice never crosses zero (the sign-safety law for integer hashes).
 */
export function fireBedMaterial(): THREE.MeshStandardNodeMaterial {
  if (bedMat) return bedMat;
  const A = objVec4('fireA');
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.95, metalness: 0 });
  m.name = 'fire-bed';
  m.colorNode = vec3(0.045, 0.036, 0.03);
  m.emissiveNode = Fn(() => {
    const t = A.w;
    const cells = mx_worley_noise_vec2(positionGeometry.xz.mul(7.5).add(vec2(40, 40)));
    const seam = float(1).sub(smoothstep(0.0, 0.16, cells.y.sub(cells.x)));
    const breath = noiseAt(vec3(positionGeometry.x.mul(0.8).add(A.x), t.mul(0.07), positionGeometry.z.mul(0.8))).w;
    const temp = saturate(seam.mul(0.55).add(breath.mul(0.35)).mul(A.z)).mul(0.72);
    const bb = texture(fireTextures().lut, vec2(temp, 0.5));
    // Exposure: the flame's own (flicker included) — the bed and the flame pulse together — on the curve.
    return bb.xyz.mul(bb.w).mul(A.y).mul(uFireEmission).mul(seam.mul(9).add(0.6));
  })();
  bedMat = m;
  return m;
}
